import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "@features/tools/components/pdf/PdfLayout";
import { commonStyles } from "@features/tools/components/pdf/CommonPdfStyles";
import MarkdownRenderer from "@features/tools/components/custom-markdown";
import { PodcastTranscriptItem } from "@packages/api/madlenAPI.schemas";
import { scaleFontSize } from "@features/tools/components/pdf/fontSizeAdjustment";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  ...commonStyles,
  transcriptContainer: {
    marginTop: 20,
    padding: 10,
    backgroundColor: "#f3f4f6",
    borderRadius: 4,
  },
  transcriptText: {
    fontFamily: "Montserrat",
    fontSize: 12,
    lineHeight: 1.5,
  },
  infoContainer: {
    marginTop: 10,
    marginBottom: 10,
  },
  infoText: {
    fontFamily: "Montserrat",
    fontSize: 12,
    marginBottom: 5,
    textAlign: "center",
  },
  questionsContainer: {
    marginTop: 20,
    padding: 10,
    backgroundColor: "#fdf4ff",
    borderRadius: 4,
    borderLeft: 3,
    borderColor: "#6b21a8",
  },
  questionText: {
    fontFamily: "Montserrat",
    fontSize: 12,
    lineHeight: 1.5,
    color: "#4c1d95",
  },
  sectionTitle: {
    fontFamily: "Montserrat",
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#6b21a8",
  },
  speakerContainer: {
    marginBottom: 8,
  },
  speakerName: {
    fontFamily: "Montserrat",
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 2,
  },
  speakerMessage: {
    fontFamily: "Montserrat",
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: 8,
  },
  primarySpeaker: {
    color: "#4c1d95", // Purple color
  },
  secondarySpeaker: {
    color: "#1e40af", // Blue color
  },
});

interface Question {
  questionText: string;
  questionAnswer: string;
}

interface PodcastProps {
  name: string | undefined;
  transcript?: string | PodcastTranscriptItem[] | undefined;
  imageUrl?: string;
  createdAt?: string;
  questions?: Question[];
  author: string;
  customizationSettings: PdfCustomizationSettings;
}

export default function PodcastPdf({
  name,
  transcript,
  imageUrl,
  createdAt,
  author,
  questions = [],
  customizationSettings,
}: PodcastProps) {
  const { t } = useTranslation();

  // Function to render transcript based on its type
  const renderTranscript = () => {
    if (!transcript) return null;

    // If transcript is a string, render it as before
    if (typeof transcript === "string") {
      return <Text style={styles.transcriptText}>{transcript}</Text>;
    }

    // If transcript is an array of PodcastTranscriptItem, render with alternating colors
    if (Array.isArray(transcript)) {
      return transcript.map((item, index) => {
        const isEven = index % 2 === 0;
        const speakerStyle = isEven
          ? styles.primarySpeaker
          : styles.secondarySpeaker;

        return (
          <View key={item.id || index} style={styles.speakerContainer}>
            <Text
              style={[
                styles.speakerName,
                speakerStyle,
                {
                  fontSize: scaleFontSize(
                    styles.speakerName.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {item.displayName || item.name || `Speaker ${index + 1}`}:
            </Text>
            <Text
              style={[
                styles.speakerMessage,
                {
                  fontSize: scaleFontSize(
                    styles.speakerMessage.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {item.message || ""}
            </Text>
          </View>
        );
      });
    }

    return null;
  };

  return (
    <PdfLayout
      title={name}
      imageUrl={imageUrl}
      headerTextColor="#6b21a8"
      topic={name}
      author={author}
      subject="Audio Lesson"
      keywords="education, audio lesson, podcast"
      creator="Madlen Teacher"
      producer="Madlen Teacher"
      language="tr"
      createdDate={createdAt}
      customizationSettings={customizationSettings}
    >
      {transcript && (
        <View style={styles.transcriptContainer}>
          <Text
            style={[
              styles.sectionTitle,
              {
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("podcast.transcript")}
          </Text>
          {renderTranscript()}
        </View>
      )}

      {questions.length > 0 && (
        <View style={styles.questionsContainer}>
          <Text
            style={[
              styles.sectionTitle,
              {
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("podcast.questions")}
          </Text>
          <MarkdownRenderer
            content={questions
              .map(
                (q, i) =>
                  `**${i + 1}**: ${q.questionText}\n${q.questionAnswer}`,
              )
              .join("\n---\n")}
            customStyle={{
              text: {
                text: {
                  ...styles.questionText,
                  fontSize: scaleFontSize(
                    styles.questionText.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />
        </View>
      )}
    </PdfLayout>
  );
}
